import { useAuthContext, usePermissions } from '/src/hooks';
import { UpsellWall } from '/src/components/UpsellWall';
import { WebP } from '/src/components/WebP';
import { FEATURES } from '/src/features/_global/constants';

import FeaturedImagePNG from '@crazyegginc/hatch/dist/images/illustration-upsell-recordings.png';
import FeaturedImageWebP from '@crazyegginc/hatch/dist/images/illustration-upsell-recordings.webp';
import RightLogoWebP from '@crazyegginc/hatch/dist/images/logo-u-s-department-of-edu.webp';
import RightLogoPNG from '@crazyegginc/hatch/dist/images/logo-u-s-department-of-edu.png';
import LeftLogoWebP from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.webp';
import LeftLogoPNG from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.png';

const TEXT_LIST = [
  {
    text: 'Rage clicks',
    number: 20,
  },
  {
    text: 'Abandoned forms',
    number: 6,
  },
  {
    text: 'Dead clicks',
    number: 4,
  },
];

const TextContent = ({ canManageBilling, ownerEmail, isStripeUser }) => {
  return (
    <>
      <UpsellWall.Title
        titleText={
          canManageBilling
            ? "We've captured 12 recordings and detected 30 issues on your site"
            : 'Request an upgrade to the Starter plan'
        }
        subtitleText={
          canManageBilling
            ? 'Start monitoring the issues today:'
            : 'See how visitors actually use your site and where they struggle.'
        }
      />
      {canManageBilling ? (
        <UpsellWall.List list={TEXT_LIST} />
      ) : (
        <>
          <p className="mb-6 text-black-pearl-500 text-sm">
            Contact your Account Admin at <strong>{ownerEmail}</strong> to upgrade and access Recordings.
          </p>
        </>
      )}
      <UpsellWall.CTA
        metricType="recordings_upsell_paywall"
        canManageBilling={canManageBilling}
        ownerEmail={ownerEmail}
        isStripeUser={isStripeUser}
      />
    </>
  );
};

const LeftContent = () => {
  return (
    <div className="flex">
      <WebP
        webp={LeftLogoWebP}
        fallback={LeftLogoPNG}
        width="80"
        height="84"
        alt="high performer winter 2024"
        className="mr-5"
      />
      <div className="flex-1">
        <UpsellWall.TestimonialText textClassNames="mb-6">
          Crazy Egg has offered us the missing piece to our website analytics. Not only do we understand how visitors
          interact with our clients&apos; websites, but we can also understand what needs to be changed on the websites
          to create a better user experience. Specifically, we love the heatmaps and recordings, which we analyze more
          than once a week.
        </UpsellWall.TestimonialText>
        <UpsellWall.TestimonialPerson name="Validated G2 Reviewer" title="Small-Business (50 or fewer employees)" />
      </div>
    </div>
  );
};

const RightContent = () => {
  return (
    <>
      <UpsellWall.TestimonialText textClassNames="mb-10">
        I was raving about and showing folks at the U.S. Department of Education the Recordings available to [Federal
        Student Aid], and everyone was fascinated.
      </UpsellWall.TestimonialText>
      <UpsellWall.TestimonialPerson
        name="Peter Kickbush"
        title="Web Analyst"
        logoWebP={RightLogoWebP}
        logoPNG={RightLogoPNG}
        logoWidth="137"
        logoHeight="40"
        logoAlt="u.s. department of education"
      />
    </>
  );
};

export function RecordingsUpsellPaywall() {
  const { currentAccount } = useAuthContext();

  const permissions = usePermissions();
  const canManageBilling = permissions.can('edit', FEATURES.BILLING).allowed;
  const isStripeUser = Boolean(currentAccount?.useStripe);

  return (
    <UpsellWall>
      <UpsellWall.TopPanel
        textContent={
          <TextContent
            canManageBilling={canManageBilling}
            ownerEmail={currentAccount.ownerEmail}
            isStripeUser={isStripeUser}
          />
        }
        imagePNG={FeaturedImagePNG}
        imageWebP={FeaturedImageWebP}
        imageWidth="558"
        imageHeight="334"
        imageAlt="recordings feature"
      />
      <UpsellWall.BottomPanel isSeparate={true} leftContent={<LeftContent />} rightContent={<RightContent />} />
    </UpsellWall>
  );
}
